import { CommonEvents, Preferences } from '@3ds/common-preferences'
import { defineStore } from 'pinia'
import { isProxy, toRaw } from 'vue'

interface UpdateInfo {
  version?: string
  message?: string
  pwaUpdateOnly?: boolean
  nativeAppUpdate?: boolean
}

interface NativeStoreState {
  isFullScreen: boolean
  showUpdate: boolean
  isSNewPlatform: boolean
  useAutoReload: boolean
  displayWindowBarUpdate: boolean
  useUpdateUI: boolean
  nativeUpdateInfo: UpdateInfo
}

export const useNativeStore = defineStore({
  id: 'native',
  state: () => ({
    isFullScreen: false,
    showUpdate: false,
    isSNewPlatform: false,
    useAutoReload: false,
    displayWindowBarUpdate: false,
    useUpdateUI: Preferences.isNativeApp(),
    nativeUpdateInfo: {} as UpdateInfo,
  } as NativeStoreState),
  getters: {
    isFullScreenMode: state => state.isFullScreen,
    isWindowBarUpdateMode: state => state.displayWindowBarUpdate,
    nativeUpdateAvailableInfo: state => state.nativeUpdateInfo,
    allowAutoReload: state => state.useAutoReload,
    showUpdateAvailable: state => state.showUpdate,
  },
  actions: {
    enterFullScreen() {
      this.isFullScreen = true
    },
    leaveFullScreen() {
      this.isFullScreen = false
    },
    checkPostponePWAUpdate() {
      // Check if has pwaUpdate awaiting then do ignore this update info
      if (this.nativeUpdateInfo && isProxy(this.nativeUpdateInfo)) {
        const rawObject = toRaw(this.nativeUpdateInfo)
        if (rawObject.pwaUpdateOnly)
          this.showUpdate = true
      }
    },
    notifyAppSWReady() {
      return new Promise((resolve, reject) => {
        CommonEvents.emitEventToNativeApp(CommonEvents.EVENTS.NOTIFY_APP_UPDATE_INSTALL, { appReady: true })
        CommonEvents.listenEventFromNativeApp('SwymEvents:checkAppPWAUpdate', (data: any) => {
          if (data && data.isSNewPlatform)
            this.isSNewPlatform = true
          resolve(data)
        })
      })
    },
    notifyUpdateInfo(updateInfo: UpdateInfo) {
      // Check if has pwaUpdate awaiting then do ignore this update info
      if (this.nativeUpdateInfo && isProxy(this.nativeUpdateInfo)) {
        const rawObject = toRaw(this.nativeUpdateInfo)
        if (rawObject.pwaUpdateOnly)
          return false
      }
      this.nativeUpdateInfo = updateInfo
      return true
    },
    notifyUpdateBanner(hasUpdate: boolean, updateInfo: UpdateInfo) {
      this.notifyUpdateInfo(updateInfo)
      this.displayWindowBarUpdate = hasUpdate
    },
    notifyUpdateAvailable(hasUpdate: boolean, updateInfo: UpdateInfo) {
      if (this.notifyUpdateInfo(updateInfo))
        this.showUpdate = hasUpdate
    },
    notifyAppToUpdate(info: any) {
      // Notify to native app if it's a native or pwa update
      // 1. if native update then continue app native update process
      // 2. if pwa update then reload app native
      if (info && info.nativeAppUpdate)
        CommonEvents.emitEventToNativeApp(CommonEvents.EVENTS.NOTIFY_APP_UPDATE_INSTALL, { nativeAppUpdate: true })
      else
        CommonEvents.emitEventToNativeApp(CommonEvents.EVENTS.NOTIFY_APP_UPDATE_INSTALL, { refreshToUpdateOnly: true })
    },
    notifyAppToPostponeUpdate(info: any) {
      this.showUpdate = false
      if (info && info.nativeAppUpdate)
        CommonEvents.emitEventToNativeApp(CommonEvents.EVENTS.NOTIFY_APP_UPDATE_INSTALL, { nativeAppUpdatePostpone: true })
    },
  },
})
